// ── Field renderer (Phase 2A + 2B) ──
// Owns #field-canvas (z-0, below glass-plate). Renders, in order:
//   1. DotGrid persistent illumination (Phase 1).
//   2. Viewer — filaments emanating from field-centre attention point (Phase 2B).
//   3. FieldBit — descending orange square, locked path D17 (Phase 2A).
//
// Order matters: the Viewer reads as part of the field substrate (behind Bit);
// the Bit reads as a discrete protagonist on top of it.
//
// All three sit BEHIND the glass plate (z-10) so they all read as muted/submerged
// through the blur. FieldBit is orange so it cuts through; Viewer is pearl-warm
// so it blends with the field.

import { DotGrid } from './dot-grid.js';
import { Viewer, viewerStageFor } from './viewer.js';
import { drawBit } from './bit-primitive.js';
import { smoothstep, lerp } from '../lib/easing.js';
import { ILLUMINATION } from '../lib/illumination-params.js';
import type { FieldClockState } from '../lib/clock.js';
import type { LogoAnchor } from './glass-renderer.js';

export class FieldRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr: number = 1;
  private dotGrid = new DotGrid();
  private viewer = new Viewer(ILLUMINATION.viewerTentacleCount);
  private alphaCap: number = 1.0;
  private dprCap: number = 2;

  private fieldBitSize: number = 32;
  private fieldBitVisible: boolean = true;

  // Shared field-anchor used by both FieldBit landing and Viewer presence.
  private fieldAnchorXFrac: number = 0.50;
  private fieldAnchorYFrac: number = 0.70;

  constructor(canvasEl: HTMLCanvasElement) {
    this.canvas = canvasEl;
    const ctx = canvasEl.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.ctx = ctx;
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  private resize(): void {
    this.dpr = Math.min(this.dprCap, window.devicePixelRatio || 1);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.round(w * this.dpr);
    this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.dotGrid.setViewport(w, h);

    // Anchor the Viewer at field-centre for the new viewport.
    this.viewer.setAnchor(w * this.fieldAnchorXFrac, h * this.fieldAnchorYFrac);
  }

  setAlphaCap(cap: number): void {
    this.alphaCap = cap;
  }

  setDprCap(cap: number): void {
    this.dprCap = cap;
    this.resize();
  }

  setFieldBitSize(size: number): void {
    this.fieldBitSize = size;
  }

  /** Hide Bit during the boot birth animation so boot-canvas owns the
   *  protagonist; restore once boot completes for the scroll-driven life. */
  setFieldBitVisible(visible: boolean): void {
    this.fieldBitVisible = visible;
  }

  /** Phase 2C: prime cellLight with ghost residue from last session. */
  applyGhostResidue(highestStoryTime: number): void {
    this.dotGrid.applyGhostResidue(
      highestStoryTime,
      ILLUMINATION.residueFraction,
      ILLUMINATION.residueAlpha,
    );
  }

  draw(clock: FieldClockState, dt: number, bitOrigin: LogoAnchor): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.ctx.clearRect(0, 0, w, h);

    // 1. Persistent illumination substrate.
    this.dotGrid.render(this.ctx, clock.highestStoryTime, this.alphaCap);

    // 2. Viewer presence (filaments at field-centre + latent glow during Hero).
    const stage = viewerStageFor(clock.glassProgress);
    this.viewer.update(dt, clock.fieldLifeTime, stage.lengthFactor);
    this.viewer.render(
      this.ctx,
      stage.alpha * this.alphaCap,
      stage.latentAlpha * this.alphaCap,
    );

    // 3. FieldBit descent (D17 locked path) + Build pulse (Beat 6 shared rhythm).
    //    Origin is the hero eyebrow square — the "main character" per AR's
    //    definition. Resolved per-frame in main.ts because it scrolls.
    this.drawFieldBit(clock.glassProgress, clock.fieldLifeTime, w, h, bitOrigin);
  }

  private drawFieldBit(
    glassProgress: number,
    fieldLifeTime: number,
    w: number,
    h: number,
    bitOrigin: LogoAnchor,
  ): void {
    // Bit lives at the eyebrow at gp 0, descends with scroll, returns at end.
    // bitOrigin is captured once at scrollY≈0 in main.ts so the descent plays
    // out in viewport coordinates, not document coords.
    // Hidden during boot — boot-canvas owns the protagonist during birth.
    if (!this.fieldBitVisible) return;
    const alpha = 1;

    const descend = smoothstep(0.05, 0.40, glassProgress);
    const ascend = smoothstep(0.65, 0.80, glassProgress);
    const travel = descend * (1 - ascend);

    const fieldCenterX = w * this.fieldAnchorXFrac;
    const fieldCenterY = h * this.fieldAnchorYFrac;

    const x = lerp(bitOrigin.x, fieldCenterX, travel);
    const y = lerp(bitOrigin.y, fieldCenterY, travel);

    // Build pulse — shared rhythm with Viewer at Beat 6 (gp 0.50..0.70).
    const buildIn = smoothstep(0.50, 0.55, glassProgress);
    const buildOut = 1 - smoothstep(0.65, 0.70, glassProgress);
    const buildPulseStrength = buildIn * buildOut;
    const pulse = 1 + buildPulseStrength * 0.18 * Math.sin(fieldLifeTime * 1.6);

    const size = lerp(bitOrigin.size, this.fieldBitSize, travel) * pulse;

    drawBit(this.ctx, x, y, size, alpha);
  }

  litCount(threshold: number = 0.15): number {
    return this.dotGrid.countLit(threshold);
  }

  totalCells(): number {
    return this.dotGrid.totalCells();
  }
}
